import React from "react";
import { buildDefaultEntryRegistrationValues } from "../components/defaultEntryRegistrationValues";
import { EntryRegistrationFormValues } from "../components/types";
import { RegistroIngresoPrefill, RegistrationMode } from "../registroIngresoNavigation";
import {
  findByDni,
  findByLastEntrance,
  findByMrz,
  findByLPQR,
  findByLPPass,
} from "../../../../store/functions/userFunction";
import UserSDK from "../../../../passapp-sdk/user";
import VehicleSDK from "../../../../passapp-sdk/vehicle";
import PassSDK from "../../../../passapp-sdk/pass";
import { useStore } from "../../../../hooks/useStore";
import DialogConfirmation from "../../../../components/common/PL/Dialog/DialogConfirmation";
import { Mrz } from "../../../../passapp-sdk/models/Mrz";
import {
  mapBackendGuestTypeToInternal,
  determineGuestTypeFromEvent,
  mapPassTypeToAccessMethod,
} from "../utils/guestTypeUtils";
import { resolveUserBirthdate, resolveUserSex } from "../utils/visitorPersonalData";
import { parseApiDate } from "../utils/apiDates";
import { toTrailerVehicleFormValues } from "../utils/trailerVehicleForm";

export type RegistroIngresoSessionTab = {
  id: string;
  mode: RegistrationMode;
  values: EntryRegistrationFormValues;
  vehicles?: any[];
  entranceEvent?: any;
  authorizations?: any[];
  authorizationsByType?: Record<string, any[]>;
  isSearching?: boolean;
};

type RegistroIngresoSessionState = {
  tabs: RegistroIngresoSessionTab[];
  activeTabId: string | null;
};

type RegistroIngresoSessionContextValue = RegistroIngresoSessionState & {
  startSession: (mode: RegistrationMode, prefill?: RegistroIngresoPrefill) => string;
  appendTab: (prefill?: RegistroIngresoPrefill, mode?: RegistrationMode) => string;
  setActiveTab: (tabId: string) => void;
  closeTab: (tabId: string) => void;
  updateTabValues: (tabId: string, values: EntryRegistrationFormValues) => void;
  clearSession: () => void;
  searchUser: (
    tabId: string,
    dni: string,
    countryCode: string,
    modeOverride?: RegistrationMode,
    prefillEntranceEvent?: any,
    passPrefill?: {
      passId?: string;
      passType?: string;
      passGuestsType?: string;
      isBuildingMismatch?: boolean;
    },
    userId?: string
  ) => Promise<any | null>;
  searchUserByMrz: (
    tabId: string,
    mrzData: { dni: string; country: string; firstname: string; lastname: string },
    modeOverride?: RegistrationMode,
    prefillEntranceEvent?: any
  ) => Promise<any | null>;
  searchUserByQr: (
    tabId: string,
    qrId: string,
    modeOverride?: RegistrationMode,
    prefillEntranceEvent?: any,
    prefetchedUserData?: any
  ) => Promise<any | null>;
  searchUserByPass: (
    tabId: string,
    qr: string,
    modeOverride?: RegistrationMode,
    prefillEntranceEvent?: any,
    prefetchedPassData?: any
  ) => Promise<any | null>;
  refetchAuthorizations: (
    tabId: string,
    type: string,
    dni: string,
    countryCode: string
  ) => Promise<void>;
  findExistingTab: (
    dni: string,
    country: string,
    mode: RegistrationMode
  ) => RegistroIngresoSessionTab | undefined;
};

type SessionAction =
  | {
      type: "START_SESSION";
      payload: { mode: RegistrationMode; prefill?: RegistroIngresoPrefill; id: string; globalDeviceOptions?: { hikvisions: Array<{ key: string; name: string }>; hikvisionsDefault: string[]; retentionTimeIn: string; retentionTimeOut: string } };
    }
  | {
      type: "APPEND_TAB";
      payload: { id: string; prefill?: RegistroIngresoPrefill; mode?: RegistrationMode; globalDeviceOptions?: { hikvisions: Array<{ key: string; name: string }>; hikvisionsDefault: string[]; retentionTimeIn: string; retentionTimeOut: string } };
    }
  | { type: "SET_ACTIVE_TAB"; payload: { tabId: string } }
  | { type: "CLOSE_TAB"; payload: { tabId: string } }
  | {
      type: "UPDATE_TAB_VALUES";
      payload: { tabId: string; values: EntryRegistrationFormValues };
    }
  | { type: "CLEAR_SESSION" }
  | { type: "SEARCH_USER_START"; payload: { tabId: string } }
  | {
      type: "SEARCH_USER_SUCCESS";
      payload: {
        tabId: string;
        userData: any;
        vehicles: any[];
        authorizations: any[];
        selectedAccessMethod: string;
        selectedInvites?: string[];
        selectedPrevalidations?: string[];
        selectedAuthorizations?: string[];
        selectedAuthorizationCompany?: string;
      };
    }
  | { type: "SEARCH_USER_FAILURE"; payload: { tabId: string } }
  | {
      type: "UPDATE_TAB_AUTHORIZATIONS";
      payload: { tabId: string; type: string; authorizations: any[] };
    };

const initialState: RegistroIngresoSessionState = {
  tabs: [],
  activeTabId: null,
};

let uidCounter = 0;
const createTabId = () => `form-${++uidCounter}`;

/**
 * Deja adelante la unidad que el guardia eligió en el modal de salida.
 *
 * `processUser` reemplaza la lista de vehículos con la del chofer (`getVehicles`), y si la unidad
 * elegida no está ahí —un tractor externo, o uno de otro ingreso— el efecto de hidratación del
 * formulario no la encuentra y reescribe la patente con otra.
 */
const keepPreselectedVehicle = (
  tab: RegistroIngresoSessionTab,
  vehicles?: any[]
): any[] | undefined => {
  if (!tab.values?.exitUnitsPreselected) return vehicles;

  const seleccionado = tab.vehicles?.[0];
  if (!seleccionado?.plate) return vehicles;

  const lista = vehicles || [];
  const yaEsta = lista.some(
    (vehicle: any) => vehicle?.id === seleccionado.id || vehicle?.plate === seleccionado.plate
  );
  return yaEsta ? lista : [seleccionado, ...lista];
};

/**
 * `getVehiclesInside` puede traer los acoplados del evento sólo como patente. Cuando termina la
 * búsqueda del chofer, `getVehicles` sí devuelve su ficha completa: la usamos para completar el
 * prefill de salida sin cambiar las unidades que eligió el guardia en el modal.
 */
const hydratePreselectedTrailerVehicles = (
  values: EntryRegistrationFormValues,
  vehicles?: any[]
) => {
  if (!values.exitUnitsPreselected || !values.trailerVehicles?.length || !vehicles?.length) {
    return values.trailerVehicles;
  }

  return values.trailerVehicles.map((trailer: any) => {
    const match = vehicles.find(
      (vehicle: any) =>
        (trailer.id && vehicle?.id === trailer.id) ||
        (trailer.vehiclePlate &&
          vehicle?.plate &&
          trailer.vehiclePlate.replace(/\s+/g, "").toUpperCase() ===
            vehicle.plate.replace(/\s+/g, "").toUpperCase())
    );
    if (!match) return trailer;

    // La ficha recién consultada es la fuente de verdad; sólo preservamos la identidad y el flag
    // de selección que llegaron desde el modal.
    return {
      ...toTrailerVehicleFormValues(match, {
        id: trailer.id || match.id,
        vehiclePlate: trailer.vehiclePlate || match.plate,
      }),
      ...(trailer.fromAuthorization ? { fromAuthorization: true } : {}),
    };
  });
};

const createSessionTab = (
  prefill?: RegistroIngresoPrefill,
  mode: RegistrationMode = "entry",
  id?: string,
  globalDeviceOptions?: { hikvisions: Array<{ key: string; name: string }>; hikvisionsDefault: string[]; retentionTimeIn: string; retentionTimeOut: string }
): RegistroIngresoSessionTab => {
  let vehicles: any[] | undefined = undefined;
  let authorizations: any[] | undefined = undefined;
  let authorizationsByType: Record<string, any[]> | undefined = undefined;

  if (mode === "exit") {
    // Cuando la salida se armó en el modal por vehículo, la unidad que eligió el guardia manda: el
    // vehículo del evento de ingreso puede ser otro (p. ej. un tractor ajeno retirando un acoplado).
    if (prefill?.exitUnitsPreselected) {
      // Sin patente la persona sale a pie: `vehicles` queda vacío a propósito. Caer al `else` acá
      // volvía a sembrar el vehículo del ingreso y deshacía lo que eligió el guardia.
      vehicles = prefill.vehiclePlate
        ? [
            {
              ...(prefill.exitVehicle || {}),
              // El sentinel "exit-vehicle-id" no es un id real: si es lo único que hay, mejor la
              // patente (ver `usableVehicleId` en useEvent.ts) — mandarlo tal cual a `updateVehicle`
              // hace que el backend explote con un id que no existe.
              id:
                prefill.selectedVehicleId && prefill.selectedVehicleId !== "exit-vehicle-id"
                  ? prefill.selectedVehicleId
                  : prefill.vehiclePlate,
              plate: prefill.vehiclePlate,
              brand: prefill.vehicleBrand ?? prefill.exitVehicle?.brand ?? "",
              model: prefill.vehicleModel ?? prefill.exitVehicle?.model ?? "",
              color: prefill.vehicleColor ?? prefill.exitVehicle?.color ?? "",
            },
          ]
        : undefined;
    } else {
      const entranceVehicle = prefill?.entranceEvent?.vehicle;
      if (entranceVehicle) {
        const vehicleWithId = {
          ...entranceVehicle,
          id:
            entranceVehicle.id ||
            entranceVehicle.plate ||
            prefill?.selectedVehicleId ||
            "exit-vehicle-id",
        };
        vehicles = [vehicleWithId];
      }
    }
  }

  return {
    id: id || createTabId(),
    mode,
    values: buildDefaultEntryRegistrationValues(prefill, globalDeviceOptions),
    vehicles,
    entranceEvent: prefill ? prefill.entranceEvent : undefined,
    authorizations,
    authorizationsByType,
  };
};

/**
 * Los cuatro caminos de egreso (manual, MRZ, QR y pass) arman el mismo usuario a partir del evento
 * de ingreso. `personInside` sólo llega cuando el evento se resolvió por `getPeopleInside`: es de
 * donde sale el legajo del chofer, que no viene con el evento.
 */
const buildExitUser = (userWithEvent: any, entranceEvent: any, personInside?: any) => {
  const resolvedGuestType = determineGuestTypeFromEvent(entranceEvent);

  return {
    ...userWithEvent,
    eventId: userWithEvent.eventId || entranceEvent?.id || entranceEvent?.eventId || "",
    entranceEvent,
    companions: [],
    vehicleType: entranceEvent?.vehicleType || "",
    observationsText: "",
    inEventComment: entranceEvent?.comment || "",
    inEventPhoto: entranceEvent?.photos?.observationsPhoto || "",
    inEventPhoto2: entranceEvent?.photos?.observationsPhoto2 || "",
    inEventPhoto3: entranceEvent?.photos?.observationsPhoto3 || "",
    peopleComment: entranceEvent?.peopleComment || "",
    // El placeholder sólo tiene sentido cuando el ingreso fue en vehículo: si la persona entró a
    // pie y el guardia cambia el medio de egreso a "Vehículo", la sección tiene que arrancar sin
    // selección para exigirle patente (con el placeholder la validación pasaba y el evento salía
    // como "car" sin ningún vehículo). Transporte fuerza `vehicleType: "car"`, así que conserva el
    // placeholder aunque el evento no traiga el objeto `vehicle`.
    selectedVehicleId:
      entranceEvent?.vehicle?.plate ||
      entranceEvent?.vehicle?.id ||
      (entranceEvent?.vehicle || entranceEvent?.vehicleType === "car" ? "exit-vehicle-id" : null),
    guestType: resolvedGuestType || entranceEvent?.guestType || "",
    // El legajo del chofer: el array manda y se resuelve después contra la empresa; el escalar es
    // el fallback para los usuarios que todavía lo tienen suelto.
    transportEmployeeNumbers:
      personInside?.transportEmployeeNumbers || userWithEvent.transportEmployeeNumbers,
    transportEmployeeNumber:
      userWithEvent.transportEmployeeNumber || personInside?.transportEmployeeNumber,
  };
};

const resolveAccessMethodFromAuthorization = (authorization: any): string => {
  const source = authorization?._source;
  if (source === "invite" || source === "authorization") {
    return "visitor";
  }

  const rawType =
    authorization?.guestsType || authorization?.type || authorization?.guestType || "";
  const normalizedType = mapBackendGuestTypeToInternal(String(rawType).toLowerCase());
  if (normalizedType === "eventual") {
    return "eventual";
  }
  if (normalizedType === "contractor") {
    return "contractor";
  }

  // Safe fallback for prevalidations without explicit type
  return "contractor";
};

const resolveAutomaticAuthorizationSelection = (authorizations: any[]) => {
  const selectableAuthorizations = (authorizations || []).filter(
    (item: any) =>
      item?.id && (item?._source === "invite" || item?._source === "authorization" || item?._source === "prevalidation")
  );

  const uniqueAuthorizations = Array.from(
    new Map(
      selectableAuthorizations.map((item: any) => [`${item._source}:${String(item.id)}`, item])
    ).values()
  );

  if (uniqueAuthorizations.length !== 1) {
    return {
      selectedAccessMethod: "",
      selectedInvites: [] as string[],
      selectedPrevalidations: [] as string[],
      selectedAuthorizations: [] as string[],
      selectedAuthorizationCompany: "",
    };
  }

  const selectedAuthorization = uniqueAuthorizations[0];
  const source = selectedAuthorization._source;
  const selectedId = String(selectedAuthorization.id);

  return {
    selectedAccessMethod: resolveAccessMethodFromAuthorization(selectedAuthorization),
    selectedInvites: source === "invite" ? [selectedId] : [],
    selectedPrevalidations: source === "prevalidation" ? [selectedId] : [],
    selectedAuthorizations: source === "authorization" ? [selectedId] : [],
    selectedAuthorizationCompany: selectedAuthorization.company || "",
  };
};

const sessionReducer = (
  state: RegistroIngresoSessionState,
  action: SessionAction
): RegistroIngresoSessionState => {
  switch (action.type) {
    case "START_SESSION": {
      const firstTab = createSessionTab(
        action.payload.prefill,
        action.payload.mode,
        action.payload.id,
        action.payload.globalDeviceOptions
      );
      return {
        tabs: [firstTab],
        activeTabId: firstTab.id,
      };
    }
    case "APPEND_TAB": {
      const nextTab = createSessionTab(
        action.payload.prefill,
        action.payload.mode ?? "entry",
        action.payload.id,
        action.payload.globalDeviceOptions
      );
      return {
        tabs: [...state.tabs, nextTab],
        activeTabId: nextTab.id,
      };
    }
    case "SET_ACTIVE_TAB": {
      if (!state.tabs.some((tab) => tab.id === action.payload.tabId)) {
        return state;
      }
      return { ...state, activeTabId: action.payload.tabId };
    }
    case "CLOSE_TAB": {
      const closedIndex = state.tabs.findIndex((tab) => tab.id === action.payload.tabId);
      if (closedIndex === -1) return state;

      const nextTabs = state.tabs.filter((tab) => tab.id !== action.payload.tabId);
      if (nextTabs.length === 0) {
        const replacementTab = createSessionTab();
        return {
          ...state,
          tabs: [replacementTab],
          activeTabId: replacementTab.id,
        };
      }

      let nextActiveTabId = state.activeTabId;
      if (state.activeTabId === action.payload.tabId) {
        const fallbackIndex = Math.max(0, closedIndex - 1);
        nextActiveTabId = nextTabs[fallbackIndex]?.id ?? nextTabs[0].id;
      }

      return {
        ...state,
        tabs: nextTabs,
        activeTabId: nextActiveTabId,
      };
    }
    case "UPDATE_TAB_VALUES": {
      return {
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.id === action.payload.tabId ? { ...tab, values: action.payload.values } : tab
        ),
      };
    }
    case "SEARCH_USER_START": {
      return {
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.id === action.payload.tabId ? { ...tab, isSearching: true } : tab
        ),
      };
    }
    case "SEARCH_USER_SUCCESS": {
      const {
        userData,
        vehicles,
        authorizations,
        selectedAccessMethod,
        selectedInvites,
        selectedPrevalidations,
        selectedAuthorizations,
        selectedAuthorizationCompany,
      } = action.payload;
      const resolvedBirthdate = resolveUserBirthdate(userData);
      const resolvedSex = resolveUserSex(userData);
      return {
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.id === action.payload.tabId
            ? {
                ...tab,
                isSearching: false,
                // Con la unidad ya elegida en el modal, la lista del chofer no debe tapar el
                // vehículo seleccionado: se conserva adelante para que la hidratación lo encuentre.
                vehicles: keepPreselectedVehicle(tab, vehicles),
                authorizations,
                authorizationsByType: selectedAccessMethod
                  ? {
                      ...tab.authorizationsByType,
                      [selectedAccessMethod]: authorizations,
                    }
                  : tab.authorizationsByType,
                values: {
                  ...tab.values,
                  id: userData.id || tab.values.id,
                  eventId: userData.eventId || tab.values.eventId,
                  firstName: userData.firstname || tab.values.firstName,
                  lastName: userData.lastname || tab.values.lastName,
                  email: userData.email || tab.values.email,
                  phone: userData.phone || tab.values.phone,
                  birthdate: resolvedBirthdate || tab.values.birthdate,
                  sex: resolvedSex || tab.values.sex,
                  visitorPhoto: userData.facePhoto || tab.values.visitorPhoto,
                  frontDNI: userData.frontDNI || tab.values.frontDNI,
                  backDNI: userData.backDNI || tab.values.backDNI,
                  documentNumber: userData.dni || tab.values.documentNumber,
                  documentCountry: userData.countryCode || tab.values.documentCountry,
                  entranceEvent: userData.entranceEvent || tab.values.entranceEvent,
                  companions: userData.companions || tab.values.companions,
                  trailerVehicles: hydratePreselectedTrailerVehicles(tab.values, vehicles),
                  // `vehicleType`/`selectedVehicleId` salen del evento de ingreso que resolvió la
                  // búsqueda, que con "retira otra persona" es el de quien retira y no el de la
                  // unidad. Si el guardia ya eligió en el modal, su elección manda.
                  vehicleType: tab.values.exitUnitsPreselected
                    ? tab.values.vehicleType
                    : userData.vehicleType || tab.values.vehicleType,
                  observationsText:
                    tab.mode === "exit"
                      ? ""
                      : userData.observationsText || tab.values.observationsText,
                  peopleComment: userData.permanentComment,
                  inEventComment: userData.inEventComment || tab.values.inEventComment,
                  // Las tres fotos del ingreso: sin la 2 y la 3 la tira de sólo lectura del egreso
                  // sólo podía mostrar la primera cuando se llegaba por escaneo.
                  inEventPhoto: userData.inEventPhoto || tab.values.inEventPhoto,
                  inEventPhoto2: userData.inEventPhoto2 || tab.values.inEventPhoto2,
                  inEventPhoto3: userData.inEventPhoto3 || tab.values.inEventPhoto3,
                  selectedVehicleId: tab.values.exitUnitsPreselected
                    ? tab.values.selectedVehicleId
                    : userData.selectedVehicleId || tab.values.selectedVehicleId,
                  selectedAccessMethod:
                    selectedAccessMethod !== undefined
                      ? selectedAccessMethod
                      : tab.values.selectedAccessMethod,
                  isEmployee: userData.isEmployee ?? tab.values.isEmployee,
                  employeeWorkplaces: userData.employeeWorkplaces ?? tab.values.employeeWorkplaces,
                  invites:
                    selectedInvites !== undefined ? selectedInvites : tab.values.invites || [],
                  prevalidation:
                    selectedPrevalidations !== undefined
                      ? selectedPrevalidations
                      : tab.values.prevalidation || [],
                  authorizations:
                    selectedAuthorizations !== undefined
                      ? selectedAuthorizations
                      : tab.values.authorizations || [],
                  authorizationCompany:
                    selectedAuthorizationCompany !== undefined
                      ? selectedAuthorizationCompany
                      : tab.values.authorizationCompany || "",
                  // Documentación del chofer (transportistas). Sólo completa lo que esté vacío: si
                  // el guardia ya cargó algo en esta sesión, gana lo suyo.
                  driverLegajoNumber:
                    tab.values.driverLegajoNumber || userData.transportEmployeeNumber || "",
                  // El legajo se guarda por empresa: el array viaja hasta el formulario, que resuelve
                  // cuál corresponde recién cuando el guardia elige la empresa de transporte.
                  transportEmployeeNumbers:
                    userData.transportEmployeeNumbers || tab.values.transportEmployeeNumbers || [],
                  driverLicenseExpirationDate:
                    tab.values.driverLicenseExpirationDate ||
                    parseApiDate(userData.driverLicenseExpirationDate),
                  driverLicensePhoto:
                    tab.values.driverLicensePhoto || userData.frontDriverLicense || null,
                  driverArtCompany:
                    tab.values.driverArtCompany ||
                    userData.artCompany ||
                    userData.artCompanyName ||
                    "",
                  driverArtPolicyNumber:
                    tab.values.driverArtPolicyNumber || userData.artPolicyNumber || "",
                  driverArtExpirationDate:
                    tab.values.driverArtExpirationDate || parseApiDate(userData.artExpirationDate),
                  driverArtPhoto: tab.values.driverArtPhoto || userData.artPhoto1 || null,
                  old: {
                    firstname: userData.firstname || "",
                    lastname: userData.lastname || "",
                    email: userData.email || "",
                    phone: userData.phone || "",
                    birthdate: resolvedBirthdate || null,
                    // Sin normalizar, a propósito: es el valor que hoy tiene el backend y contra el
                    // que `updateUserIfNeeded` decide si hay que actualizar. Si acá se guardara
                    // normalizado, un usuario con un género en formato viejo (`M`, `1`) se vería
                    // bien en el desplegable pero nunca se migraría al valor canónico, porque el
                    // formulario y el baseline darían iguales.
                    sex: userData.sex || "",
                    // Baseline de la documentación: sin esto, cada submit reenviaría por updateUser
                    // los mismos valores que acaba de leer.
                    transportEmployeeNumber: userData.transportEmployeeNumber || "",
                    driverLicenseExpirationDate: userData.driverLicenseExpirationDate || "",
                    artCompany: userData.artCompany || userData.artCompanyName || "",
                    artPolicyNumber: userData.artPolicyNumber || "",
                    artExpirationDate: userData.artExpirationDate || "",
                  },
                  blacklistedData: userData.blacklistedData ?? null,
                  isBlackList: Boolean(userData.isBlackList),
                  blacklistAuthorization: undefined,
                },
              }
            : tab
        ),
      };
    }
    case "SEARCH_USER_FAILURE": {
      return {
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.id === action.payload.tabId
            ? {
                ...tab,
                isSearching: false,
                authorizations: [],
                vehicles: [],
              }
            : tab
        ),
      };
    }
    case "UPDATE_TAB_AUTHORIZATIONS": {
      return {
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.id === action.payload.tabId
            ? {
                ...tab,
                isSearching: false,
                authorizations: action.payload.authorizations,
                authorizationsByType: {
                  ...tab.authorizationsByType,
                  [action.payload.type]: action.payload.authorizations,
                },
              }
            : tab
        ),
      };
    }
    case "CLEAR_SESSION":
      return initialState;
    default:
      return state;
  }
};

export const RegistroIngresoSessionContext =
  React.createContext<RegistroIngresoSessionContextValue | null>(null);

export const RegistroIngresoSessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(sessionReducer, initialState);
  const { authStore } = useStore();
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const userSdk = React.useMemo(() => new UserSDK(), []);
  const vehicleSdk = React.useMemo(() => new VehicleSDK(), []);
  const passSdk = React.useMemo(() => new PassSDK(), []);

  const stateRef = React.useRef(state);
  React.useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const fetchEntryAuthorizations = React.useCallback(
    async (dni: string, countryCode: string): Promise<any[]> => {
      if (!dni || !countryCode) {
        return [];
      }

      const [visitorResult, contractorResult, eventualResult] = await Promise.allSettled([
        userSdk.getAuthorizations(authStore.token, dni, countryCode),
        userSdk.getPrevalidations(authStore.token, "contractor", dni, countryCode),
        userSdk.getPrevalidations(authStore.token, "eventual", dni, countryCode),
      ]);

      const normalized: any[] = [];

      if (visitorResult.status === "fulfilled") {
        normalized.push(
          ...(visitorResult.value?.data?.authorizations || []).map((a: any) => ({
            ...a,
            _source: "authorization",
          })),
          ...(visitorResult.value?.data?.invites || []).map((i: any) => ({
            ...i,
            _source: "invite",
          }))
        );
      }

      if (contractorResult.status === "fulfilled") {
        normalized.push(
          ...(contractorResult.value?.data || []).map((p: any) => ({
            ...p,
            _source: "prevalidation",
            type: p.type || "contractor",
          }))
        );
      }

      if (eventualResult.status === "fulfilled") {
        normalized.push(
          ...(eventualResult.value?.data || []).map((p: any) => ({
            ...p,
            _source: "prevalidation",
            type: p.type || "eventual",
          }))
        );
      }

      return Array.from(
        new Map(normalized.map((item: any) => [`${item._source}:${String(item.id)}`, item])).values()
      );
    },
    [authStore.token, userSdk]
  );

  const searchUser = React.useCallback(
    (
      tabId: string,
      dni: string,
      countryCode: string,
      modeOverride?: RegistrationMode,
      prefillEntranceEvent?: any,
      passPrefill?: {
        passId?: string;
        passType?: string;
        passGuestsType?: string;
        isBuildingMismatch?: boolean;
      },
      userId?: string
    ): Promise<any | null> => {
      dispatch({ type: "SEARCH_USER_START", payload: { tabId } });

      const tab = stateRef.current.tabs.find((t) => t.id === tabId);
      const isExit = modeOverride === "exit" || tab?.mode === "exit";
      const selectedAccessMethod = tab?.values?.selectedAccessMethod;

      return new Promise((resolve) => {
        const processUser = async (enrichedUser: any) => {
          if (!enrichedUser) {
            dispatch({ type: "SEARCH_USER_FAILURE", payload: { tabId } });
            resolve(null);
            return;
          }

          const passId = passPrefill?.passId || tab?.values?.passId;
          const passType = passPrefill?.passType || tab?.values?.passType;
          const passGuestsType = passPrefill?.passGuestsType || tab?.values?.passGuestsType;
          const isBuildingMismatch = passPrefill?.isBuildingMismatch ?? tab?.values?.isBuildingMismatch;

          const passAccessMethod = (passType && !isBuildingMismatch)
            ? mapPassTypeToAccessMethod(passType, passGuestsType)
            : undefined;

          const activeAccessMethod = isBuildingMismatch
            ? ""
            : (passAccessMethod || selectedAccessMethod || enrichedUser?.guestType);

          let authorizations: any[] = [];
          let vehicles: any[] = [];
          const isEmployee =
            enrichedUser?.employee === true || enrichedUser?.guestType === "employee";

          try {
            // El ingreso ya no precarga: busca por patente con findVehicles/selectVehicle (ver
            // VehicleContentUserDataForm). El egreso sigue conociendo su vehículo de forma
            // determinística (el del evento de entrada) y usa getVehicles para completar la ficha.
            const vehiclePromise = isExit && enrichedUser.id
              ? vehicleSdk.getVehicles(authStore.token, enrichedUser.id)
              : Promise.resolve({ data: [] });

            if (isEmployee) {
              // Skip authorizations fetch
            } else if (!isExit) {
              authorizations = await fetchEntryAuthorizations(dni, countryCode);
              // Las autorizaciones de transporte salen de transport/getTransportAuthorizations, no
              // de getPrevalidations: pedirlas acá es una llamada al pedo.
            } else if (activeAccessMethod && activeAccessMethod !== "transport") {
              if (activeAccessMethod === "visitor" || activeAccessMethod === "visitante") {
                const authRes = await userSdk.getAuthorizations(authStore.token, dni, countryCode);
                const rawAuths = (authRes?.data?.authorizations || []).map((a: any) => ({
                  ...a,
                  _source: "authorization",
                }));
                const rawInvites = (authRes?.data?.invites || []).map((i: any) => ({
                  ...i,
                  _source: "invite",
                }));
                authorizations = [...rawAuths, ...rawInvites];
              } else {
                const authRes = await userSdk.getPrevalidations(
                  authStore.token,
                  activeAccessMethod,
                  dni,
                  countryCode
                );
                authorizations = authRes?.data || [];
              }
            }

            // If exit mode and we have an entrance event, include the authorizations that let them in
            const event =
              enrichedUser.entranceEvent || prefillEntranceEvent || tab?.values?.entranceEvent;
            if (isExit && event) {
              const ent = event.event || event;
              const entranceAuths = (ent.authorizations || []).map((a: any) => ({
                ...a,
                _source: "authorization",
              }));
              const entranceInvites: any[] = [];
              const entrancePrevals = (ent.prevalidations || ent.prevalidation || []).map(
                (p: any) => ({
                  ...p,
                  _source: "prevalidation",
                })
              );

              (ent.invites || []).forEach((i: any) => {
                // Una confirmación en guardia no es una autorización: entra al form como
                // employeeConfirmation (ver buildDefaultEntryRegistrationValues) y se muestra en
                // "Seleccionado en guardia". Sumarla acá la duplicaba como autorización vigente —
                // se veía sobre todo en el egreso del acompañante de transporte, cuya única
                // "autorización" de ingreso es justamente esa confirmación.
                if (i.type === "guard") {
                  return;
                }
                if (
                  i.type === "contractor" ||
                  i.type === "eventual" ||
                  i.guestsType === "contractor" ||
                  i.guestsType === "eventual"
                ) {
                  entrancePrevals.push({
                    ...i,
                    _source: "prevalidation",
                  });
                } else {
                  entranceInvites.push({
                    ...i,
                    _source: "invite",
                  });
                }
              });

              const allEntranceAuths = [...entranceAuths, ...entranceInvites, ...entrancePrevals];

              // Merge, avoiding duplicates by ID if possible
              allEntranceAuths.forEach((ea) => {
                if (!authorizations.find((a) => a.id === ea.id)) {
                  authorizations.push(ea);
                }
              });
            }

            const vehicleRes = await vehiclePromise;
            vehicles = vehicleRes?.data || [];

            const entranceVehicle = enrichedUser?.entranceEvent?.vehicle;
            if (isExit && entranceVehicle) {
              const entranceVehicleId =
                entranceVehicle.id || entranceVehicle.plate || "exit-vehicle-id";
              const alreadyExists = vehicles.some(
                (v) =>
                  v.id === entranceVehicleId ||
                  (entranceVehicle.plate && v.plate === entranceVehicle.plate)
              );
              if (!alreadyExists) {
                const vehicleWithId = {
                  ...entranceVehicle,
                  id: entranceVehicleId,
                };
                vehicles = [vehicleWithId, ...vehicles];
              }
            }
          } catch (error) {
            console.error("Error fetching auxiliary data:", error);
          }

          const shouldAutoSelectSingleAuthorization = !isExit && !isEmployee && !isBuildingMismatch;
          const automaticSelection = shouldAutoSelectSingleAuthorization
            ? resolveAutomaticAuthorizationSelection(authorizations)
            : null;

          dispatch({
            type: "SEARCH_USER_SUCCESS",
            payload: {
              tabId,
              userData: {
                ...enrichedUser,
                isEmployee,
                employeeWorkplaces: enrichedUser?.workplaces || [],
              },
              vehicles,
              authorizations,
              selectedAccessMethod: isBuildingMismatch
                ? ""
                : (isEmployee
                    ? "employee"
                    : automaticSelection
                      ? automaticSelection.selectedAccessMethod
                      : activeAccessMethod
                        ? mapBackendGuestTypeToInternal(activeAccessMethod)
                        : ""),
              selectedInvites: automaticSelection ? automaticSelection.selectedInvites : undefined,
              selectedPrevalidations: automaticSelection
                ? automaticSelection.selectedPrevalidations
                : undefined,
              selectedAuthorizations: automaticSelection
                ? automaticSelection.selectedAuthorizations
                : undefined,
              selectedAuthorizationCompany: automaticSelection
                ? automaticSelection.selectedAuthorizationCompany
                : undefined,
            },
          });

          resolve(enrichedUser);
        };

        // For exit: same flow as entrance (findByDni → findUser → createInitialEvent)
        // but wrap processUser to enrich with entrance data before dispatching.

        const enrichExitUser = async (userWithEvent: any) => {
          if (!userWithEvent) {
            processUser(null);
            return;
          }

          const entranceEvent =
            prefillEntranceEvent || tab?.values?.entranceEvent || (tab as any)?.entranceEvent;
          if (entranceEvent) {
            processUser(buildExitUser(userWithEvent, entranceEvent));
            return;
          }

          try {
            const fromTimestamp = Math.floor(Date.now() / 1000) - 7 * 24 * 60 * 60; // 7 days ago
            const toTimestamp = Math.floor(Date.now() / 1000);
            const res = await userSdk.getPeopleInside(authStore.token, fromTimestamp, toTimestamp);

            const matchedPerson = (res?.data?.data || []).find(
              (p: any) =>
                (p.id && String(p.id) === String(userWithEvent.id)) ||
                (p.dni &&
                  String(p.dni) === String(userWithEvent.dni) &&
                  String(p.countryCode).toLowerCase() ===
                    String(userWithEvent.countryCode).toLowerCase())
            );

            const entranceEvent: any = matchedPerson?.event;

            processUser(buildExitUser(userWithEvent, entranceEvent, matchedPerson));
          } catch (err) {
            console.warn("Error fetching people inside for manual exit:", err);
            processUser(userWithEvent);
          }
        };

        if (isExit) {
          if (userId) {
            findByLastEntrance(authStore.token, userId, dni, countryCode, enrichExitUser, undefined);
          } else {
            findByDni(authStore.token, dni, countryCode, enrichExitUser, undefined);
          }
        } else {
          findByDni(authStore.token, dni, countryCode, processUser, undefined);
        }
      });
    },
    [authStore.token, userSdk, vehicleSdk, fetchEntryAuthorizations]
  );

  const searchUserByMrz = React.useCallback(
    (
      tabId: string,
      mrzData: { dni: string; country: string; firstname: string; lastname: string },
      modeOverride?: RegistrationMode,
      prefillEntranceEvent?: any
    ): Promise<any | null> => {
      dispatch({ type: "SEARCH_USER_START", payload: { tabId } });

      const tab = stateRef.current.tabs.find((t) => t.id === tabId);
      const isExit = modeOverride === "exit" || tab?.mode === "exit";
      const selectedAccessMethod = tab?.values?.selectedAccessMethod;

      return new Promise((resolve) => {
        const processUser = async (enrichedUser: any) => {
          if (!enrichedUser) {
            dispatch({ type: "SEARCH_USER_FAILURE", payload: { tabId } });
            resolve(null);
            return;
          }

          let authorizations: any[] = [];
          let vehicles: any[] = [];
          const isEmployee =
            enrichedUser?.employee === true || enrichedUser?.guestType === "employee";

          try {
            // El ingreso ya no precarga: busca por patente con findVehicles/selectVehicle (ver
            // VehicleContentUserDataForm). El egreso sigue conociendo su vehículo de forma
            // determinística (el del evento de entrada) y usa getVehicles para completar la ficha.
            const vehiclePromise = isExit && enrichedUser.id
              ? vehicleSdk.getVehicles(authStore.token, enrichedUser.id)
              : Promise.resolve({ data: [] });

            if (isEmployee) {
              // Skip authorizations fetch
            } else if (!isExit) {
              authorizations = await fetchEntryAuthorizations(mrzData.dni, mrzData.country);
            } else {
              const type = selectedAccessMethod || enrichedUser?.guestType;
              if (type === "visitor" || type === "visitante") {
                const authRes = await userSdk.getAuthorizations(
                  authStore.token,
                  mrzData.dni,
                  mrzData.country
                );
                const rawAuths = (authRes?.data?.authorizations || []).map((a: any) => ({
                  ...a,
                  _source: "authorization",
                }));
                const rawInvites = (authRes?.data?.invites || []).map((i: any) => ({
                  ...i,
                  _source: "invite",
                }));
                authorizations = [...rawAuths, ...rawInvites];
                // Transporte no usa getPrevalidations (ver el flujo de búsqueda por DNI).
              } else if (type && type !== "transport") {
                const authRes = await userSdk.getPrevalidations(
                  authStore.token,
                  type,
                  mrzData.dni,
                  mrzData.country
                );
                authorizations = authRes?.data || [];
              }
            }

            // If exit mode and we have an entrance event, include the authorizations that let them in
            const event =
              enrichedUser.entranceEvent || prefillEntranceEvent || tab?.values?.entranceEvent;
            if (isExit && event) {
              const ent = event.event || event;
              const entranceAuths = (ent.authorizations || []).map((a: any) => ({
                ...a,
                _source: "authorization",
              }));
              const entranceInvites: any[] = [];
              const entrancePrevals = (ent.prevalidations || ent.prevalidation || []).map(
                (p: any) => ({
                  ...p,
                  _source: "prevalidation",
                })
              );

              (ent.invites || []).forEach((i: any) => {
                // Una confirmación en guardia no es una autorización: entra al form como
                // employeeConfirmation (ver buildDefaultEntryRegistrationValues) y se muestra en
                // "Seleccionado en guardia". Sumarla acá la duplicaba como autorización vigente —
                // se veía sobre todo en el egreso del acompañante de transporte, cuya única
                // "autorización" de ingreso es justamente esa confirmación.
                if (i.type === "guard") {
                  return;
                }
                if (
                  i.type === "contractor" ||
                  i.type === "eventual" ||
                  i.guestsType === "contractor" ||
                  i.guestsType === "eventual"
                ) {
                  entrancePrevals.push({
                    ...i,
                    _source: "prevalidation",
                  });
                } else {
                  entranceInvites.push({
                    ...i,
                    _source: "invite",
                  });
                }
              });

              const allEntranceAuths = [...entranceAuths, ...entranceInvites, ...entrancePrevals];

              // Merge, avoiding duplicates by ID if possible
              allEntranceAuths.forEach((ea) => {
                if (!authorizations.find((a) => a.id === ea.id)) {
                  authorizations.push(ea);
                }
              });
            }

            const vehicleRes = await vehiclePromise;
            vehicles = vehicleRes?.data || [];

            const entranceVehicle = enrichedUser?.entranceEvent?.vehicle;
            if (isExit && entranceVehicle) {
              const entranceVehicleId =
                entranceVehicle.id || entranceVehicle.plate || "exit-vehicle-id";
              const alreadyExists = vehicles.some(
                (v) =>
                  v.id === entranceVehicleId ||
                  (entranceVehicle.plate && v.plate === entranceVehicle.plate)
              );
              if (!alreadyExists) {
                const vehicleWithId = {
                  ...entranceVehicle,
                  id: entranceVehicleId,
                };
                vehicles = [vehicleWithId, ...vehicles];
              }
            }
          } catch (error) {
            console.error("Error fetching auxiliary data (MRZ):", error);
          }

          const shouldAutoSelectSingleAuthorization = !isExit && !isEmployee;
          const automaticSelection = shouldAutoSelectSingleAuthorization
            ? resolveAutomaticAuthorizationSelection(authorizations)
            : null;

          dispatch({
            type: "SEARCH_USER_SUCCESS",
            payload: {
              tabId,
              userData: {
                ...enrichedUser,
                isEmployee,
                employeeWorkplaces: enrichedUser?.workplaces || [],
              },
              vehicles,
              authorizations,
              selectedAccessMethod: isEmployee
                ? "employee"
                : automaticSelection
                  ? automaticSelection.selectedAccessMethod
                  : mapBackendGuestTypeToInternal(enrichedUser?.guestType || selectedAccessMethod),
              selectedInvites: automaticSelection ? automaticSelection.selectedInvites : undefined,
              selectedPrevalidations: automaticSelection
                ? automaticSelection.selectedPrevalidations
                : undefined,
              selectedAuthorizations: automaticSelection
                ? automaticSelection.selectedAuthorizations
                : undefined,
              selectedAuthorizationCompany: automaticSelection
                ? automaticSelection.selectedAuthorizationCompany
                : undefined,
            },
          });

          resolve(enrichedUser);
        };

        const mrzPayload: Mrz = {
          dni: mrzData.dni,
          country: mrzData.country,
          firstname: mrzData.firstname,
          lastname: mrzData.lastname,
        };

        // For exit: same flow as entrance (findByMrz → findUser → createInitialEvent)
        // but wrap processUser to enrich with entrance data before dispatching.

        const enrichExitUser = async (userWithEvent: any) => {
          if (!userWithEvent) {
            processUser(null);
            return;
          }

          const entranceEvent =
            prefillEntranceEvent || tab?.values?.entranceEvent || (tab as any)?.entranceEvent;
          if (entranceEvent) {
            processUser(buildExitUser(userWithEvent, entranceEvent));
            return;
          }

          try {
            const fromTimestamp = Math.floor(Date.now() / 1000) - 7 * 24 * 60 * 60; // 7 days ago
            const toTimestamp = Math.floor(Date.now() / 1000);
            const res = await userSdk.getPeopleInside(authStore.token, fromTimestamp, toTimestamp);

            const matchedPerson = (res?.data?.data || []).find(
              (p: any) =>
                (p.id && String(p.id) === String(userWithEvent.id)) ||
                (p.dni &&
                  String(p.dni) === String(userWithEvent.dni) &&
                  String(p.countryCode).toLowerCase() ===
                    String(userWithEvent.countryCode).toLowerCase())
            );

            const entranceEvent: any = matchedPerson?.event;

            processUser(buildExitUser(userWithEvent, entranceEvent, matchedPerson));
          } catch (err) {
            console.warn("Error fetching people inside for manual exit (MRZ):", err);
            processUser(userWithEvent);
          }
        };

        if (isExit) {
          findByMrz(authStore.token, mrzPayload, enrichExitUser, undefined);
        } else {
          findByMrz(authStore.token, mrzPayload, processUser, undefined);
        }
      });
    },
    [authStore.token, userSdk, vehicleSdk, fetchEntryAuthorizations]
  );

  const searchUserByQr = React.useCallback(
    (
      tabId: string,
      qrId: string,
      modeOverride?: RegistrationMode,
      prefillEntranceEvent?: any,
      prefetchedUserData?: any
    ): Promise<any | null> => {
      dispatch({ type: "SEARCH_USER_START", payload: { tabId } });

      const tab = stateRef.current.tabs.find((t) => t.id === tabId);
      const isExit = modeOverride === "exit" || tab?.mode === "exit";
      const selectedAccessMethod = tab?.values?.selectedAccessMethod;

      return new Promise((resolve) => {
        const processUser = async (enrichedUser: any) => {
          if (!enrichedUser) {
            dispatch({ type: "SEARCH_USER_FAILURE", payload: { tabId } });
            resolve(null);
            return;
          }

          let authorizations: any[] = [];
          let vehicles: any[] = [];
          const isEmployee =
            enrichedUser?.employee === true || enrichedUser?.guestType === "employee";

          try {
            // El ingreso ya no precarga: busca por patente con findVehicles/selectVehicle (ver
            // VehicleContentUserDataForm). El egreso sigue conociendo su vehículo de forma
            // determinística (el del evento de entrada) y usa getVehicles para completar la ficha.
            const vehiclePromise = isExit && enrichedUser.id
              ? vehicleSdk.getVehicles(authStore.token, enrichedUser.id)
              : Promise.resolve({ data: [] });

            if (isEmployee) {
              // Skip authorizations fetch
            } else if (!isExit) {
              authorizations = await fetchEntryAuthorizations(
                enrichedUser.dni,
                enrichedUser.countryCode
              );
            } else {
              const type = selectedAccessMethod || enrichedUser?.guestType;
              if (type === "visitor" || type === "visitante") {
                const authRes = await userSdk.getAuthorizations(
                  authStore.token,
                  enrichedUser.dni,
                  enrichedUser.countryCode
                );
                const rawAuths = (authRes?.data?.authorizations || []).map((a: any) => ({
                  ...a,
                  _source: "authorization",
                }));
                const rawInvites = (authRes?.data?.invites || []).map((i: any) => ({
                  ...i,
                  _source: "invite",
                }));
                authorizations = [...rawAuths, ...rawInvites];
                // Transporte no usa getPrevalidations (ver el flujo de búsqueda por DNI).
              } else if (type && type !== "transport") {
                const authRes = await userSdk.getPrevalidations(
                  authStore.token,
                  type,
                  enrichedUser.dni,
                  enrichedUser.countryCode
                );
                authorizations = authRes?.data || [];
              }
            }

            // If exit mode and we have an entrance event, include the authorizations that let them in
            const event =
              enrichedUser.entranceEvent || prefillEntranceEvent || tab?.values?.entranceEvent;
            if (isExit && event) {
              const ent = event.event || event;
              const entranceAuths = (ent.authorizations || []).map((a: any) => ({
                ...a,
                _source: "authorization",
              }));
              const entranceInvites: any[] = [];
              const entrancePrevals = (ent.prevalidations || ent.prevalidation || []).map(
                (p: any) => ({
                  ...p,
                  _source: "prevalidation",
                })
              );

              (ent.invites || []).forEach((i: any) => {
                // Una confirmación en guardia no es una autorización: entra al form como
                // employeeConfirmation (ver buildDefaultEntryRegistrationValues) y se muestra en
                // "Seleccionado en guardia". Sumarla acá la duplicaba como autorización vigente —
                // se veía sobre todo en el egreso del acompañante de transporte, cuya única
                // "autorización" de ingreso es justamente esa confirmación.
                if (i.type === "guard") {
                  return;
                }
                if (
                  i.type === "contractor" ||
                  i.type === "eventual" ||
                  i.guestsType === "contractor" ||
                  i.guestsType === "eventual"
                ) {
                  entrancePrevals.push({
                    ...i,
                    _source: "prevalidation",
                  });
                } else {
                  entranceInvites.push({
                    ...i,
                    _source: "invite",
                  });
                }
              });

              const allEntranceAuths = [...entranceAuths, ...entranceInvites, ...entrancePrevals];

              allEntranceAuths.forEach((ea) => {
                if (!authorizations.find((a) => a.id === ea.id)) {
                  authorizations.push(ea);
                }
              });
            }

            const vehicleRes = await vehiclePromise;
            vehicles = vehicleRes?.data || [];

            const entranceVehicle = enrichedUser?.entranceEvent?.vehicle;
            if (isExit && entranceVehicle) {
              const entranceVehicleId =
                entranceVehicle.id || entranceVehicle.plate || "exit-vehicle-id";
              const alreadyExists = vehicles.some(
                (v) =>
                  v.id === entranceVehicleId ||
                  (entranceVehicle.plate && v.plate === entranceVehicle.plate)
              );
              if (!alreadyExists) {
                const vehicleWithId = {
                  ...entranceVehicle,
                  id: entranceVehicleId,
                };
                vehicles = [vehicleWithId, ...vehicles];
              }
            }
          } catch (error) {
            console.error("Error fetching auxiliary data (QR):", error);
          }

          const shouldAutoSelectSingleAuthorization = !isExit && !isEmployee;
          const automaticSelection = shouldAutoSelectSingleAuthorization
            ? resolveAutomaticAuthorizationSelection(authorizations)
            : null;

          dispatch({
            type: "SEARCH_USER_SUCCESS",
            payload: {
              tabId,
              userData: {
                ...enrichedUser,
                isEmployee,
                employeeWorkplaces: enrichedUser?.workplaces || [],
              },
              vehicles,
              authorizations,
              selectedAccessMethod: isEmployee
                ? "employee"
                : automaticSelection
                  ? automaticSelection.selectedAccessMethod
                  : mapBackendGuestTypeToInternal(enrichedUser?.guestType || selectedAccessMethod),
              selectedInvites: automaticSelection ? automaticSelection.selectedInvites : undefined,
              selectedPrevalidations: automaticSelection
                ? automaticSelection.selectedPrevalidations
                : undefined,
              selectedAuthorizations: automaticSelection
                ? automaticSelection.selectedAuthorizations
                : undefined,
              selectedAuthorizationCompany: automaticSelection
                ? automaticSelection.selectedAuthorizationCompany
                : undefined,
            },
          });

          resolve(enrichedUser);
        };

        const enrichExitUser = async (userWithEvent: any) => {
          if (!userWithEvent) {
            processUser(null);
            return;
          }

          const entranceEvent =
            prefillEntranceEvent || tab?.values?.entranceEvent || (tab as any)?.entranceEvent;
          if (entranceEvent) {
            processUser(buildExitUser(userWithEvent, entranceEvent));
            return;
          }

          try {
            const fromTimestamp = Math.floor(Date.now() / 1000) - 7 * 24 * 60 * 60; // 7 days ago
            const toTimestamp = Math.floor(Date.now() / 1000);
            const res = await userSdk.getPeopleInside(authStore.token, fromTimestamp, toTimestamp);

            const matchedPerson = (res?.data?.data || []).find(
              (p: any) =>
                (p.id && String(p.id) === String(userWithEvent.id)) ||
                (p.dni &&
                  String(p.dni) === String(userWithEvent.dni) &&
                  String(p.countryCode).toLowerCase() ===
                    String(userWithEvent.countryCode).toLowerCase())
            );

            const entranceEvent: any = matchedPerson?.event;

            processUser(buildExitUser(userWithEvent, entranceEvent, matchedPerson));
          } catch (err) {
            console.warn("Error fetching people inside for manual exit (QR):", err);
            processUser(userWithEvent);
          }
        };

        const handleApiError = (err: any) => {
          dispatch({ type: "SEARCH_USER_FAILURE", payload: { tabId } });
          setErrorMessage(err || "Error al buscar el usuario");
          setErrorDialogOpen(true);
          resolve(null);
        };

        if (prefetchedUserData) {
          if (isExit) {
            enrichExitUser(prefetchedUserData);
          } else {
            processUser(prefetchedUserData);
          }
        } else {
          if (isExit) {
            findByLPQR(authStore.token, qrId, enrichExitUser, undefined, handleApiError);
          } else {
            findByLPQR(authStore.token, qrId, processUser, undefined, handleApiError);
          }
        }
      });
    },
    [authStore.token, userSdk, vehicleSdk, fetchEntryAuthorizations]
  );

  const searchUserByPass = React.useCallback(
    (
      tabId: string,
      qr: string,
      modeOverride?: RegistrationMode,
      prefillEntranceEvent?: any,
      prefetchedPassData?: any
    ): Promise<any | null> => {
      dispatch({ type: "SEARCH_USER_START", payload: { tabId } });

      const tab = stateRef.current.tabs.find((t) => t.id === tabId);
      const isExit = modeOverride === "exit" || tab?.mode === "exit";
      const selectedAccessMethod = tab?.values?.selectedAccessMethod;

      return new Promise((resolve) => {
        const processUser = async (enrichedUser: any) => {
          if (!enrichedUser) {
            dispatch({ type: "SEARCH_USER_FAILURE", payload: { tabId } });
            resolve(null);
            return;
          }

          let authorizations: any[] = [];
          let vehicles: any[] = [];
          const isEmployee =
            enrichedUser?.employee === true || enrichedUser?.guestType === "employee";
          const passAccessMethod = enrichedUser.isBuildingMismatch
            ? ""
            : mapPassTypeToAccessMethod(enrichedUser.passType, enrichedUser.passGuestsType);

          try {
            // El ingreso ya no precarga: busca por patente con findVehicles/selectVehicle (ver
            // VehicleContentUserDataForm). El egreso sigue conociendo su vehículo de forma
            // determinística (el del evento de entrada) y usa getVehicles para completar la ficha.
            const vehiclePromise = isExit && enrichedUser.id
              ? vehicleSdk.getVehicles(authStore.token, enrichedUser.id)
              : Promise.resolve({ data: [] });

            if (isEmployee) {
              // Skip authorizations fetch
            } else if (!isExit && !enrichedUser.isBuildingMismatch) {
              authorizations = await fetchEntryAuthorizations(
                enrichedUser.dni,
                enrichedUser.countryCode
              );
            } else {
              const type = passAccessMethod || selectedAccessMethod || enrichedUser?.guestType;
              if (type === "visitor" || type === "visitante") {
                const authRes = await userSdk.getAuthorizations(
                  authStore.token,
                  enrichedUser.dni,
                  enrichedUser.countryCode
                );
                const rawAuths = (authRes?.data?.authorizations || []).map((a: any) => ({
                  ...a,
                  _source: "authorization",
                }));
                const rawInvites = (authRes?.data?.invites || []).map((i: any) => ({
                  ...i,
                  _source: "invite",
                }));
                authorizations = [...rawAuths, ...rawInvites];
                // Transporte no usa getPrevalidations (ver el flujo de búsqueda por DNI).
              } else if (type && type !== "transport") {
                const authRes = await userSdk.getPrevalidations(
                  authStore.token,
                  type,
                  enrichedUser.dni,
                  enrichedUser.countryCode
                );
                authorizations = authRes?.data || [];
              }
            }

            // If exit mode and we have an entrance event, include the authorizations that let them in
            const event =
              enrichedUser.entranceEvent || prefillEntranceEvent || tab?.values?.entranceEvent;
            if (isExit && event) {
              const ent = event.event || event;
              const entranceAuths = (ent.authorizations || []).map((a: any) => ({
                ...a,
                _source: "authorization",
              }));
              const entranceInvites: any[] = [];
              const entrancePrevals = (ent.prevalidations || ent.prevalidation || []).map(
                (p: any) => ({
                  ...p,
                  _source: "prevalidation",
                })
              );

              (ent.invites || []).forEach((i: any) => {
                // Una confirmación en guardia no es una autorización: entra al form como
                // employeeConfirmation (ver buildDefaultEntryRegistrationValues) y se muestra en
                // "Seleccionado en guardia". Sumarla acá la duplicaba como autorización vigente —
                // se veía sobre todo en el egreso del acompañante de transporte, cuya única
                // "autorización" de ingreso es justamente esa confirmación.
                if (i.type === "guard") {
                  return;
                }
                if (
                  i.type === "contractor" ||
                  i.type === "eventual" ||
                  i.guestsType === "contractor" ||
                  i.guestsType === "eventual"
                ) {
                  entrancePrevals.push({
                    ...i,
                    _source: "prevalidation",
                  });
                } else {
                  entranceInvites.push({
                    ...i,
                    _source: "invite",
                  });
                }
              });

              const allEntranceAuths = [...entranceAuths, ...entranceInvites, ...entrancePrevals];

              allEntranceAuths.forEach((ea) => {
                if (!authorizations.find((a) => a.id === ea.id)) {
                  authorizations.push(ea);
                }
              });
            }

            const vehicleRes = await vehiclePromise;
            vehicles = vehicleRes?.data || [];

            const entranceVehicle = enrichedUser?.entranceEvent?.vehicle;
            if (isExit && entranceVehicle) {
              const entranceVehicleId =
                entranceVehicle.id || entranceVehicle.plate || "exit-vehicle-id";
              const alreadyExists = vehicles.some(
                (v) =>
                  v.id === entranceVehicleId ||
                  (entranceVehicle.plate && v.plate === entranceVehicle.plate)
              );
              if (!alreadyExists) {
                const vehicleWithId = {
                  ...entranceVehicle,
                  id: entranceVehicleId,
                };
                vehicles = [vehicleWithId, ...vehicles];
              }
            }
          } catch (error) {
            console.error("Error fetching auxiliary data (Pass):", error);
          }

          const shouldAutoSelectSingleAuthorization =
            !isExit && !isEmployee && !enrichedUser.isBuildingMismatch;
          const automaticSelection = shouldAutoSelectSingleAuthorization
            ? resolveAutomaticAuthorizationSelection(authorizations)
            : null;

          dispatch({
            type: "SEARCH_USER_SUCCESS",
            payload: {
              tabId,
              userData: {
                ...enrichedUser,
                isEmployee,
                employeeWorkplaces: enrichedUser?.workplaces || [],
              },
              vehicles,
              authorizations,
              selectedAccessMethod: isEmployee
                ? "employee"
                : automaticSelection
                  ? automaticSelection.selectedAccessMethod
                  : passAccessMethod ||
                    mapBackendGuestTypeToInternal(enrichedUser?.guestType || selectedAccessMethod),
              selectedInvites: automaticSelection ? automaticSelection.selectedInvites : undefined,
              selectedPrevalidations: automaticSelection
                ? automaticSelection.selectedPrevalidations
                : undefined,
              selectedAuthorizations: automaticSelection
                ? automaticSelection.selectedAuthorizations
                : undefined,
              selectedAuthorizationCompany: automaticSelection
                ? automaticSelection.selectedAuthorizationCompany
                : undefined,
            },
          });

          resolve(enrichedUser);
        };

        const enrichExitUser = async (userWithEvent: any) => {
          if (!userWithEvent) {
            processUser(null);
            return;
          }

          const entranceEvent =
            prefillEntranceEvent || tab?.values?.entranceEvent || (tab as any)?.entranceEvent;
          if (entranceEvent) {
            processUser(buildExitUser(userWithEvent, entranceEvent));
            return;
          }

          try {
            const fromTimestamp = Math.floor(Date.now() / 1000) - 7 * 24 * 60 * 60; // 7 days ago
            const toTimestamp = Math.floor(Date.now() / 1000);
            const res = await userSdk.getPeopleInside(authStore.token, fromTimestamp, toTimestamp);

            const matchedPerson = (res?.data?.data || []).find(
              (p: any) =>
                (p.id && String(p.id) === String(userWithEvent.id)) ||
                (p.dni &&
                  String(p.dni) === String(userWithEvent.dni) &&
                  String(p.countryCode).toLowerCase() ===
                    String(userWithEvent.countryCode).toLowerCase())
            );

            const entranceEvent: any = matchedPerson?.event;

            processUser(buildExitUser(userWithEvent, entranceEvent, matchedPerson));
          } catch (err) {
            console.warn("Error fetching people inside for manual exit (Pass):", err);
            processUser(userWithEvent);
          }
        };

        const handleApiError = (err: any) => {
          dispatch({ type: "SEARCH_USER_FAILURE", payload: { tabId } });
          setErrorMessage(err || "Error al buscar el usuario");
          setErrorDialogOpen(true);
          resolve(null);
        };

        if (prefetchedPassData) {
          let guest = prefetchedPassData.guest;
          if (!guest) {
            if (prefetchedPassData.guests) {
              guest = prefetchedPassData.guests[0];
            } else {
              guest = prefetchedPassData;
            }
          }
          if (!guest) {
            dispatch({ type: "SEARCH_USER_FAILURE", payload: { tabId } });
            resolve(null);
            return;
          }

          const inviteId =
            prefetchedPassData.inviteId || prefetchedPassData.id || guest?.inviteId || guest?.id;
          const inviteCountry = prefetchedPassData.country || guest?.country || guest?.countryCode;

          const passConfig = {
            userAddon: {
              isPass: inviteId,
              isUnknown:
                !(
                  authStore.country === inviteCountry ||
                  authStore.country?.country === inviteCountry
                ) || false,
            },
          };

          // Replicate store/functions/userFunction's createEvent logic for initial event
          const userSdkObj = new UserSDK();
          const newEventObj = new (require("../../../../passapp-sdk/newEvent").default)();

          newEventObj
            .createInitialEvent(authStore.token, guest.id)
            .then((response: any) => {
              const firstname = guest.firstname === "-pending-" ? "" : guest.firstname;
              const lastname = guest.lastname === "-pending-" ? "" : guest.lastname;
              const enrichedGuest = {
                ...guest,
                comment: "",
                permanentComment: guest.comment ?? "",
                commentReject: "",
                lastname,
                firstname,
                eventId: response.data.id,
                authorizations: [],
                isResident: guest.residencesInfo?.length !== 0,
                isBlackList: guest.blacklistedData,
                old: {
                  lastname,
                  firstname,
                  countryCode: guest.countryCode,
                  dni: guest.dni,
                  driverLicenseExpirationDate: guest.driverLicenseExpirationDate || "",
                  workMaterials: guest.workMaterials || "",
                  artCompanyName: guest.artCompanyName || "",
                  artPolicyNumber: guest.artPolicyNumber || "",
                  artExpirationDate: guest.artExpirationDate || "",
                  criminalRecordIssueDate: guest.artExpirationDate || "",
                  professionTypes: guest.professionTypes || "",
                },
                ...passConfig.userAddon,
                passType: prefetchedPassData.type,
                passId: inviteId,
                passGuestsType: prefetchedPassData.guestsType,
                passHost: prefetchedPassData.host || prefetchedPassData.manager,
                passCompany: prefetchedPassData.company,
                isBuildingMismatch: prefetchedPassData.isBuildingMismatch,
              };

              if (isExit) {
                enrichExitUser(enrichedGuest);
              } else {
                processUser(enrichedGuest);
              }
            })
            .catch((err: any) => {
              console.error(err);
              dispatch({ type: "SEARCH_USER_FAILURE", payload: { tabId } });
              resolve(null);
            });
        } else {
          findByLPPass(
            authStore.token,
            authStore.country,
            qr,
            isExit ? enrichExitUser : processUser,
            undefined,
            handleApiError
          );
        }
      });
    },
    [authStore.token, authStore.country, userSdk, vehicleSdk, fetchEntryAuthorizations]
  );

  const refetchAuthorizations = React.useCallback(
    async (tabId: string, type: string, dni: string, countryCode: string) => {
      if (!dni || !countryCode || !type) return;

      const tab = stateRef.current.tabs.find((t) => t.id === tabId);
      if (tab?.authorizationsByType?.[type]) {
        dispatch({
          type: "UPDATE_TAB_AUTHORIZATIONS",
          payload: { tabId, type, authorizations: tab.authorizationsByType[type] },
        });
        return;
      }

      dispatch({ type: "SEARCH_USER_START", payload: { tabId } });

      try {
        let normalizedAuths: any[] = [];
        if (type === "visitor") {
          const authRes = await userSdk.getAuthorizations(authStore.token, dni, countryCode);
          const rawAuths = (authRes?.data?.authorizations || []).map((a: any) => ({
            ...a,
            _source: "authorization",
          }));
          const rawInvites = (authRes?.data?.invites || []).map((i: any) => ({
            ...i,
            _source: "invite",
          }));
          normalizedAuths = [...rawAuths, ...rawInvites];
        } else {
          const backendType = type;
          const authRes = await userSdk.getPrevalidations(
            authStore.token,
            backendType,
            dni,
            countryCode
          );
          normalizedAuths = authRes?.data || [];
        }
        dispatch({
          type: "UPDATE_TAB_AUTHORIZATIONS",
          payload: { tabId, type, authorizations: normalizedAuths },
        });
      } catch (error) {
        console.error("Error fetching authorizations:", error);
        dispatch({
          type: "UPDATE_TAB_AUTHORIZATIONS",
          payload: { tabId, type, authorizations: [] },
        });
      }
    },
    [authStore.token, userSdk]
  );

  const startSession = React.useCallback(
    (mode: RegistrationMode, prefill?: RegistroIngresoPrefill): string => {
      const id = createTabId();
      const globalDeviceOptions = { hikvisions: authStore.hikvisions, hikvisionsDefault: authStore.hikvisionsDefault, retentionTimeIn: authStore.retentionTimeIn, retentionTimeOut: authStore.retentionTimeOut };
      dispatch({ type: "START_SESSION", payload: { mode, prefill, id, globalDeviceOptions } });
      return id;
    },
    [authStore.hikvisions, authStore.hikvisionsDefault, authStore.retentionTimeIn, authStore.retentionTimeOut]
  );

  const appendTab = React.useCallback(
    (prefill?: RegistroIngresoPrefill, mode?: RegistrationMode): string => {
      const id = createTabId();
      const globalDeviceOptions = { hikvisions: authStore.hikvisions, hikvisionsDefault: authStore.hikvisionsDefault, retentionTimeIn: authStore.retentionTimeIn, retentionTimeOut: authStore.retentionTimeOut };
      dispatch({ type: "APPEND_TAB", payload: { id, prefill, mode, globalDeviceOptions } });
      return id;
    },
    [authStore.hikvisions, authStore.hikvisionsDefault, authStore.retentionTimeIn, authStore.retentionTimeOut]
  );

  const setActiveTab = React.useCallback((tabId: string) => {
    dispatch({ type: "SET_ACTIVE_TAB", payload: { tabId } });
  }, []);

  const closeTab = React.useCallback((tabId: string) => {
    dispatch({ type: "CLOSE_TAB", payload: { tabId } });
  }, []);

  const updateTabValues = React.useCallback(
    (tabId: string, values: EntryRegistrationFormValues) => {
      dispatch({ type: "UPDATE_TAB_VALUES", payload: { tabId, values } });
    },
    []
  );

  const findExistingTab = React.useCallback(
    (dni: string, country: string, mode: RegistrationMode) => {
      return state.tabs.find(
        (tab) =>
          tab.mode === mode &&
          tab.values.documentNumber?.trim().toLowerCase() === dni.trim().toLowerCase() &&
          tab.values.documentCountry?.trim().toLowerCase() === country.trim().toLowerCase()
      );
    },
    [state.tabs]
  );

  const clearSession = React.useCallback(() => {
    dispatch({ type: "CLEAR_SESSION" });
  }, []);

  const value = React.useMemo(
    () => ({
      ...state,
      startSession,
      appendTab,
      setActiveTab,
      closeTab,
      updateTabValues,
      clearSession,
      searchUser,
      searchUserByMrz,
      searchUserByQr,
      searchUserByPass,
      refetchAuthorizations,
      findExistingTab,
    }),
    [
      state,
      startSession,
      appendTab,
      setActiveTab,
      closeTab,
      updateTabValues,
      clearSession,
      searchUser,
      searchUserByMrz,
      searchUserByQr,
      searchUserByPass,
      refetchAuthorizations,
      findExistingTab,
    ]
  );

  return (
    <RegistroIngresoSessionContext.Provider value={value}>
      {children}
      <DialogConfirmation
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        onConfirm={() => setErrorDialogOpen(false)}
        title="Error"
        message={errorMessage}
        confirmLabel="Aceptar"
        cancelLabel="Cancelar"
      />
    </RegistroIngresoSessionContext.Provider>
  );
};

export const useRegistroIngresoSession = () => {
  const context = React.useContext(RegistroIngresoSessionContext);
  if (!context) {
    throw new Error("useRegistroIngresoSession must be used inside RegistroIngresoSessionProvider");
  }
  return context;
};
